'use client';
import React, { useState, useCallback, useEffect } from 'react';
import { apiURL } from '../../../../common/urls';
import { classNames } from '../../../../util/classNames';
import interactionResponse from 'await-interaction-response';

type Props = {
  bodyItem: {
    readonly answers: readonly {
      readonly id: string;
      readonly text: string;
      readonly voteCount: number;
    }[];
    readonly id: any;
    readonly isClosed: boolean;
    readonly title: string;
    readonly voteCount: number;
  };
};

function BodyItemPoll(props: Props) {
  const { bodyItem } = props;
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [skipVote, setSkipVote] = useState(false);
  const [votesCount] = useState(
    bodyItem.answers.reduce((prev, answer) => prev + answer.voteCount, 0),
  );
  const [voted, setVoted] = useState(false);
  const vote = useCallback(async () => {
    if (!selectedAnswer) return;
    const answer = bodyItem.answers.find((a) => a.id === selectedAnswer);
    if (!answer) return console.error('Ungültige Antwort');

    interactionResponse().then(async () => {
      window.dataLayer?.push({
        event: 'custom_event',
        event_name: 'poll_vote',
        poll_vote: {
          poll_question: bodyItem.title,
          poll_answer: answer.text,
          poll_id: bodyItem.id,
        },
      });

      const response = await fetch(apiURL + '/polls/' + bodyItem.id, {
        method: 'POST',
        body: JSON.stringify({
          answer_id: answer.id,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (response.status !== 200) {
        console.error('Failed to vote');
      }
    });

    setVoted(true);
    setSelectedAnswer(answer.id);
  }, [bodyItem, selectedAnswer]);
  const onAnswerChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedAnswer(e.currentTarget.value);
    },
    [],
  );

  useEffect(() => {
    if (selectedAnswer) {
      vote();
    }
  }, [selectedAnswer, vote]);

  const renderAnswer = (answer: Props['bodyItem']['answers'][number]) => {
    let { voteCount: answerVotesCount } = answer;
    const showResult = voted || skipVote || bodyItem.isClosed;
    const isSelectedAnswer = answer.id === selectedAnswer;

    if (showResult) {
      if (isSelectedAnswer) answerVotesCount += 1;
    }
    return (
      <div
        className={classNames(
          'flex flex-row items-start gap-m mx-0 p-l border-[1px] border-solid  rounded-xl cursor-pointer',
          isSelectedAnswer
            ? 'border-primaryColor bg-lightPink dark:bg-adsBackground'
            : 'border-lightGrey',
          showResult
            ? 'cursor-default'
            : 'cursor-pointer hover:border-primaryColor',
        )}
        key={'result' + answer.id}
        onClick={() => {
          if (!showResult) {
            setSelectedAnswer(answer.id);
            vote();
          }
        }}
      >
        {showResult && (
          <div className="text-tintColor font-semibold w-10">
            {answerVotesCount}
          </div>
        )}
        {!showResult && (
          <input
            id={answer.id}
            type="radio"
            name="poll_answer"
            className="before:block before:w-[70%] before:h-[70%] before:my-[15%] before:mx-auto before:rounded-[50%] before:checked:bg-tintColor m-0 box-content appearance-none w-5 h-5 border-[1px] border-solid border-grey rounded-[50%] outline-none block cursor-pointer checked:border-tintColor checked:bg-lightGrey group-hover:border-tintColor flex-shrink-0"
          />
        )}
        <div className="flex-1">
          <div
            className={classNames(
              'grid transition-all',
              showResult
                ? 'grid-rows-[1fr] max-h-fit pb-2'
                : 'grid-rows-[0fr] max-h-0',
            )}
          >
            <div className="flex flex-1 bg-lightGrey rounded-xl mt-[9px] overflow-hidden flex-grow">
              <div
                className="h-1 rounded-xl flex-1 bg-tintColor transition-all duration-300 delay-150"
                style={{
                  flex: showResult ? answerVotesCount : 0,
                }}
              />
              <div
                style={{
                  flex: votesCount - answerVotesCount,
                }}
              />
            </div>
          </div>
          <label className="flex cursor-pointer" htmlFor={answer.id}>
            {answer.text}
          </label>
        </div>
      </div>
    );
  };

  if (!bodyItem || !bodyItem.title || !Array.isArray(bodyItem.answers)) {
    console.log('Invalid poll received');
    return null;
  }

  const answers = bodyItem.answers.map((answer) => renderAnswer(answer));
  const userCanVote = !bodyItem.isClosed && !voted && !skipVote;

  return (
    <div
      style={{
        order: 100,
      }}
      className="my-xl mx-0 w-full max-w-[750px]"
    >
      <div className="mb-5">
        <span className="block font-semibold text-xl">{bodyItem.title}</span>
        {/* {!userCanVote && (
          <span className="votesCount text-xs font-normal text-grey">
            {votesCount} Stimmen
          </span>
        )} */}
      </div>
      <div className="mt-5 mx-0 flex flex-col gap-m">{answers}</div>
      <div className="flex justify-between">
        {/* {userCanVote && (
          <button
            className="cursor-pointer border-none bg-tintColor p-3 text-base font-semibold text-white text-center self-center disabled:bg-grey rounded"
            disabled={!selectedAnswer}
            onClick={vote}
          >
            Abstimmen
          </button>
        )} */}
        {userCanVote && (
          <button
            className="cursor-pointer border-none bg-[transparent] text-base font-semibold text-darkgrey dark:text-lightGrey rounded-xl hover:text-tintColor mt-xl"
            onClick={() => setSkipVote(true)}
          >
            Ergebnis anzeigen
          </button>
        )}
      </div>
    </div>
  );
}

export default BodyItemPoll;
